import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCalendarLight,
  PiPencilSimpleLineLight,
  PiFileLight,
  PiHardDrivesLight,
  PiUserLight,
  PiBriefcaseLight,
} from 'react-icons/pi';
import { Field, FieldGrid } from '../../shared/brand/Field';
import CollaboratorStack from '../../shared/access/CollaboratorStack';
import { formatBytes, formatDateTime } from '../../../utils/functions';

export interface DocumentInfoPerson {
  user_uuid: string;
  name: string;
  initials: string;
  access: string;
  email?: string | null;
  avatar_url?: string | null;
}

export interface DocumentInfo {
  name: string;
  type?: string;
  size?: number;
  createdAt?: string;
  modifiedAt?: string;
  uploadedBy?: string;
  matterTitle?: string;
  folderPath?: string;
  sharedWith?: DocumentInfoPerson[];
}

/**
 * The document's facts as a horizontal strip above it (S11, files-3).
 *
 * This was a modal behind the header's Info button - a second window over
 * the document to read four facts. Readers everywhere else show their facts
 * as a strip across the top (ReaderComponent), never a side column or a
 * popup; Files now does the same. Info toggles the strip, and the choice is
 * remembered.
 *
 * One quiet row of label-over-value facts, wrapping on narrow screens, then
 * the details (location, uploader, matter, who it is shared with) inline.
 */
const DocumentInfoStrip: React.FC<{ info: DocumentInfo }> = ({ info }) => {
  const { t } = useTranslation();
  const shared = info.sharedWith ?? [];

  return (
    <div
      className="flex-shrink-0 border-b border-hairline-soft bg-surface-soft/60 px-4 py-3"
      data-testid="document-info-strip"
    >
      <FieldGrid columns={4}>
        <Field icon={<PiFileLight />} label={t('Type')} value={info.type} />
        <Field
          icon={<PiHardDrivesLight />}
          label={t('Size')}
          value={info.size != null && info.size > 0 ? formatBytes(info.size) : undefined}
        />
        <Field
          icon={<PiCalendarLight />}
          label={t('Uploaded / created')}
          value={info.createdAt ? formatDateTime(info.createdAt) : undefined}
        />
        <Field
          icon={<PiPencilSimpleLineLight />}
          label={t('Last modified')}
          value={info.modifiedAt ? formatDateTime(info.modifiedAt) : undefined}
        />
      </FieldGrid>
      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        {info.folderPath && (
          <span className="min-w-0 truncate text-fg-body" title={info.folderPath}>
            <span className="text-fg-muted">{t('Location')}: </span>{info.folderPath}
          </span>
        )}
        {info.uploadedBy && (
          <span className="flex items-center gap-1.5 text-fg-body">
            <PiUserLight className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
            <span className="text-fg-muted">{t('Uploaded by')}:</span> {info.uploadedBy}
          </span>
        )}
        {info.matterTitle && (
          <span className="flex items-center gap-1.5 text-fg-body">
            <PiBriefcaseLight className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
            {info.matterTitle}
          </span>
        )}
        <span className="flex items-center gap-2">
          <span className="text-fg-muted">{t('Shared with')}:</span>
          {shared.length > 0
            ? <CollaboratorStack collaborators={shared} max={6} size="sm" />
            : <span className="text-fg-muted">{t('Not shared')}</span>}
        </span>
      </div>
    </div>
  );
};

export default DocumentInfoStrip;
